import {effect, Injectable, signal, WritableSignal} from "@angular/core";
import {Rol, RutasUsuarioInterface} from "../interfaces/login.interface";
import {HttpClient} from "@angular/common/http";
import {RandomSessionIdService} from "../../../guards/random-session-id.service";
import {environment} from "../../../../environments/environment.development";

const apiVucUrl = environment.apiVucUrl
@Injectable({
    providedIn: 'root'
})
export class UserMenuService {
    private _rutas: WritableSignal<RutasUsuarioInterface[]> = signal([])
    constructor(private http: HttpClient, private randomSessionIdService: RandomSessionIdService) {
    }
    /*
     Process flow:

     1. Initialize rutas and parentMenuMap:
     rutas -> []
     parentMenuMap -> {}

     2. Loop through each item in resp.body:
     i -> { idUsuarioMenu, idMenuPadre, ... }

     ┌───────────────────────────────────────────────┐
     │ For each item (i)                             │
     │                                               │
     │ i['queryParams'] = { sessionId, idSolicitudTipo } │
     │                                               │
     │ if (i.idMenuPadre != null) {                  │
     │     // Child menu                             │
     │     ┌─────────────────────────────────────┐   │
     │     │ Look up parent in parentMenuMap     │   │
     │     │ parentMenu = parentMenuMap.get(i.idMenuPadre) │
     │     │ if (parentMenu) {                   │   │
     │     │     Add i to parentMenu.items       │   │
     │     │     parentMenu.items.push(i)        │   │
     │     │ } else {                            │   │
     │     │     Add i to rutas                  │   │
     │     │     rutas.push(i)                   │   │
     │     │ }                                   │   │
     │     └─────────────────────────────────────┘   │
     │ } else {                                      │
     │     // Parent menu                            │
     │     ┌─────────────────────────────────────┐   │
     │     │ Initialize i.items if undefined     │   │
     │     │ i.items = i.items || []             │   │
     │     │ Add i to parentMenuMap              │   │
     │     │ parentMenuMap.set(i.idUsuarioMenu, i) │ │
     │     │ Add i to rutas                      │   │
     │     │ rutas.push(i)                       │   │
     │     └─────────────────────────────────────┘   │
     │ }                                             │
     └───────────────────────────────────────────────┘

     3. Ensure all child menus are placed correctly:
     ┌───────────────────────────────────────────────┐
     │ For each item (i)                             │
     │ if (i.idMenuPadre != null) {                  │
     │     Look up parent in parentMenuMap           │
     │     parentMenu = parentMenuMap.get(i.idMenuPadre) │
     │     if (parentMenu) {                         │
     │         Ensure i is in parentMenu.items       │
     │         parentMenu.items.push(i) if not already present │
     │     }                                         │
     │ }                                             │
     └───────────────────────────────────────────────┘

     4. Set the final structured menu:
     this.rutas.set(rutas)
     */
    getMenusPorRol(roles: string[] | null) {
        const parentMenuSessionId = this.randomSessionIdService.randomSessionId();
        this.http.get<RutasUsuarioInterface[]>(apiVucUrl + `api/v1.0/menus/${roles}`, {observe: 'response'})
            .subscribe(
                resp => {
                    let rutas: RutasUsuarioInterface[] = []
                    let parentMenuMap: Map<number, RutasUsuarioInterface> = new Map();
                    //Procesar y validar menus padres
                    for (let i of resp.body!) {
                        i['queryParams'] = {sessionId: parentMenuSessionId};
                        if (i.idMenuPadre != null) { // Child menu
                            let parentMenu = parentMenuMap.get(i.idMenuPadre);
                            if (parentMenu) {
                                parentMenu.items = parentMenu.items || [];
                                parentMenu.items.push(i);
                            } else {
                                rutas.push(i); // In case the parent menu is not found, we just push the child menu
                            }
                        } else { // Parent menu
                            i.items = i.items || [];
                            parentMenuMap.set(i.idUsuarioMenu, i);
                            rutas.push(i);
                        }
                    }
                    //Procesar y validar menus hijos
                    for (let i of resp.body!) {
                        if (i.idMenuPadre != null) { // Child menu
                            let parentMenu = parentMenuMap.get(i.idMenuPadre);
                            if (parentMenu) {
                                parentMenu.items = parentMenu.items || [];
                                if (!parentMenu.items.includes(i)) {
                                    parentMenu.items.push(i);
                                }
                            }
                        }
                    }
                    // Agregar menus
                    this._rutas.set(rutas);
                }
            )
    }
    getUniqueIDs(roles: Rol[]): string[] {
        const uniqueIDs = roles.map(role => role.uniqueID);
        return [...new Set(uniqueIDs)];
    }
    get rutas(): WritableSignal<RutasUsuarioInterface[]> {
        return this._rutas;
    }
    set rutas(value: WritableSignal<RutasUsuarioInterface[]>) {
        this._rutas = value;
    }
    /**
     * Sync only once on startup
     */
    loadRutasEffect = effect(() => {
        const rutasSession = sessionStorage.getItem('routes');
        if (rutasSession) {
            this.rutas.set(JSON.parse(rutasSession));
        }
        console.log(`routes status: ${rutasSession != null}`);
    }, {allowSignalWrites: true})
    /**
     * Sync routes when changes.
     */
    syncRutasEffect = effect(() => {
        console.log(`syncRutasEffect call`)
        sessionStorage.setItem("routes", JSON.stringify(this.rutas()));
        console.log(`routes status: ${this.rutas() != null}`);
    })
}
